import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

export const api = axios.create({ baseURL: API });

// Normaliza los errores de validación de FastAPI (422): `detail` puede ser un
// array de objetos {type,loc,msg,input,url}. Si se pasa tal cual a un toast/JSX,
// React lanza el error #31 ("objects are not valid as a React child").
// Aquí lo convertimos SIEMPRE en un string legible.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const d = error?.response?.data?.detail;
    if (Array.isArray(d)) {
      error.response.data.detail = d
        .map((e) => (e && typeof e === "object" ? e.msg || JSON.stringify(e) : String(e)))
        .join(" · ");
    } else if (d && typeof d === "object") {
      error.response.data.detail = d.msg || JSON.stringify(d);
    }
    return Promise.reject(error);
  }
);

export const fileUrl = (p) => (p ? (p.startsWith("http") ? p : `${BACKEND_URL}${p}`) : "");
export const thumbUrl = (p, w = 900) => (p && p.startsWith("/api/files/") ? `${BACKEND_URL}${p}?w=${w}` : fileUrl(p));

export function setAuthToken(token) {
  if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete api.defaults.headers.common["Authorization"];
}

// bootstrap token on load
const saved = typeof localStorage !== "undefined" ? localStorage.getItem("lci_token") : null;
if (saved) setAuthToken(saved);

export const getCourses = () => api.get("/courses").then((r) => r.data);
export const getOffer = () => api.get("/offer").then((r) => r.data);
export const postContact = (payload) => api.post("/contact", payload).then((r) => r.data);
export const getTakenSlots = (dia) => api.get("/bookings/taken", { params: { dia } }).then((r) => r.data);
export const postBooking = (payload) => api.post("/bookings", payload).then((r) => r.data);

export const getMyCourses = () => api.get("/me/courses").then((r) => r.data);
export const getDashboard = () => api.get("/me/dashboard").then((r) => r.data);
export const getCourseProgress = (slug) => api.get(`/courses/${slug}/progress`).then((r) => r.data);
export const toggleModule = (slug, leccion_id) => api.post(`/courses/${slug}/progress/toggle`, { leccion_id }).then((r) => r.data);
export const savePosition = (slug, unit_key) => api.post(`/courses/${slug}/progress/position`, { unit_key }).then((r) => r.data);
export const downloadCertificado = () => api.get("/certificado", { params: { origin: window.location.origin }, responseType: "blob" }).then((r) => r.data);
export const verificarCert = (code) => api.get(`/verificar/${code}`).then((r) => r.data);
export const getRanking = () => api.get("/ranking").then((r) => r.data);

export const getPosts = () => api.get("/posts").then((r) => r.data);
export const createPost = (texto) => api.post("/posts", { texto }).then((r) => r.data);
export const getWeeklyCalls = () => api.get("/weekly-calls").then((r) => r.data);
export const reserveCall = (id) => api.post(`/weekly-calls/${id}/reserve`).then((r) => r.data);

export function wsUrl(path) {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}${path}`;
}
export const getToken = () => (typeof localStorage !== "undefined" ? localStorage.getItem("lci_token") : null);

// ---- admin ----
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminCreateCourse = (data) => api.post("/admin/courses", data).then((r) => r.data);
export const adminUpdateCourse = (slug, data) => api.put(`/admin/courses/${slug}`, data).then((r) => r.data);
export const adminDeleteCourse = (slug) => api.delete(`/admin/courses/${slug}`).then((r) => r.data);
export const adminReorderCourses = (slugs) => api.post("/admin/courses/reorder", { slugs }).then((r) => r.data);
export const adminUpdateOffer = (data) => api.put("/admin/offer", data).then((r) => r.data);
export const adminGetCalls = () => api.get("/admin/weekly-calls").then((r) => r.data);
export const adminCreateCall = (data) => api.post("/admin/weekly-calls", data).then((r) => r.data);
export const adminUpdateCall = (id, data) => api.put(`/admin/weekly-calls/${id}`, data).then((r) => r.data);
export const adminDeleteCall = (id) => api.delete(`/admin/weekly-calls/${id}`).then((r) => r.data);
export const adminBookings = () => api.get("/admin/bookings").then((r) => r.data);
export const adminLeads = () => api.get("/admin/leads").then((r) => r.data);
export const adminToggleLead = (id) => api.patch(`/admin/leads/${id}`).then((r) => r.data);
export const adminUsers = () => api.get("/admin/users").then((r) => r.data);
export const adminChat = () => api.get("/admin/chat").then((r) => r.data);
export const adminDeleteChat = (id) => api.delete(`/admin/chat/${id}`).then((r) => r.data);
export const adminDeletePost = (id) => api.delete(`/admin/posts/${id}`).then((r) => r.data);
export const adminAnalytics = () => api.get("/admin/analytics").then((r) => r.data);
export const adminSetPassword = (id, password) => api.post(`/admin/users/${id}/set-password`, { password }).then((r) => r.data);

// ---- visit tracking ----
export const trackVisit = (payload) => api.post("/track/visit", payload).then((r) => r.data).catch(() => {});

// ---- portal de empleo ----
export const getEmpleos = () => api.get("/empleos").then((r) => r.data);
export const adminEmpleos = () => api.get("/admin/empleos").then((r) => r.data);
export const adminCreateEmpleo = (data) => api.post("/admin/empleos", data).then((r) => r.data);
export const adminUpdateEmpleo = (id, data) => api.put(`/admin/empleos/${id}`, data).then((r) => r.data);
export const adminDeleteEmpleo = (id) => api.delete(`/admin/empleos/${id}`).then((r) => r.data);

// ---- zona de recursos ----
export const getRecursos = () => api.get("/recursos").then((r) => r.data);
export const adminRecursos = () => api.get("/admin/recursos").then((r) => r.data);
export const adminCreateRecurso = (data) => api.post("/admin/recursos", data).then((r) => r.data);
export const adminUpdateRecurso = (id, data) => api.put(`/admin/recursos/${id}`, data).then((r) => r.data);
export const adminDeleteRecurso = (id) => api.delete(`/admin/recursos/${id}`).then((r) => r.data);

export const adminReorderRecursos = (ids) => api.post("/admin/recursos/reorder", { slugs: ids }).then((r) => r.data);

// ---- notificaciones + badges ----
export const getBadges = () => api.get("/me/badges").then((r) => r.data);
export const markSeen = (seccion) => api.post(`/me/seen/${seccion}`).then((r) => r.data);
export const getNotifications = () => api.get("/notifications").then((r) => r.data);
export const markNotifsRead = () => api.post("/me/notifications/read").then((r) => r.data);
export const adminGetNotifications = () => api.get("/admin/notifications").then((r) => r.data);
export const adminSendNotification = (data) => api.post("/admin/notifications", data).then((r) => r.data);
export const adminDeleteNotification = (id) => api.delete(`/admin/notifications/${id}`).then((r) => r.data);
export const getAnnouncement = () => api.get("/me/announcement").then((r) => r.data.announcement);
export const markAnnouncementSeen = () => api.post("/me/announcement/seen").then((r) => r.data);

// ---- web push ----
export const getVapidKey = () => api.get("/push/vapid-public-key").then((r) => r.data.key);
export const pushSubscribe = (subscription) => api.post("/push/subscribe", { subscription }).then((r) => r.data);
export const pushUnsubscribe = (subscription) => api.post("/push/unsubscribe", { subscription }).then((r) => r.data);

// ---- solicitudes ----
export const crearSolicitud = (data) => api.post("/solicitudes", data).then((r) => r.data);
export const misSolicitudes = () => api.get("/me/solicitudes").then((r) => r.data);
export const adminSolicitudes = () => api.get("/admin/solicitudes").then((r) => r.data);
export const adminToggleSolicitud = (id) => api.patch(`/admin/solicitudes/${id}`).then((r) => r.data);
export const adminResponderSolicitud = (id, mensaje) => api.post(`/admin/solicitudes/${id}/responder`, { mensaje }).then((r) => r.data);
export const adminDeleteSolicitud = (id) => api.delete(`/admin/solicitudes/${id}`).then((r) => r.data);
export const adminSetAccess = (id, data) => api.patch(`/admin/users/${id}/access`, data).then((r) => r.data);
export const adminCreateUser = (data) => api.post("/admin/users", data).then((r) => r.data);
export const adminDeleteUser = (id) => api.delete(`/admin/users/${id}`).then((r) => r.data);
export const adminEditUser = (id, data) => api.patch(`/admin/users/${id}`, data).then((r) => r.data);
export const adminResetDevices = (id) => api.post(`/admin/users/${id}/reset-devices`).then((r) => r.data);
export const requestDeviceUnlock = (email) => api.post("/device-unlock-request", { email }).then((r) => r.data);
export const adminDeviceRequests = () => api.get("/admin/device-requests").then((r) => r.data);
export const adminApproveDeviceRequest = (id) => api.post(`/admin/device-requests/${id}/approve`).then((r) => r.data);
export const updateProfile = (data) => api.patch("/me/profile", data).then((r) => r.data);

// ---- anuncios (canal Comunidad) ----
export const getAnuncios = () => api.get("/anuncios").then((r) => r.data);
export const adminCreateAnuncio = (data) => api.post("/admin/anuncios", data).then((r) => r.data);
export const adminDeleteAnuncio = (id) => api.delete(`/admin/anuncios/${id}`).then((r) => r.data);

// ---- uploads (chunked) ----
const _uploadListeners = new Set();
export function onUploadProgress(cb) { _uploadListeners.add(cb); return () => _uploadListeners.delete(cb); }
function emitUpload(state) { _uploadListeners.forEach((cb) => { try { cb(state); } catch { /* noop */ } }); }

export async function uploadFileChunked(file, onProgress) {
  const uploadId =
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const CHUNK = 1 * 1024 * 1024; // 1MB — evita límites de tamaño de petición del ingress
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK));
  const report = (pct) => { if (onProgress) onProgress(pct); emitUpload({ active: true, filename: file.name, pct }); };
  const postWithRetry = async (path, fd, tries = 3) => {
    let lastErr;
    for (let t = 0; t < tries; t++) {
      try { return await api.post(path, fd, { timeout: 120000 }); }
      catch (e) { lastErr = e; await new Promise((r) => setTimeout(r, 800 * (t + 1))); }
    }
    throw lastErr;
  };
  emitUpload({ active: true, filename: file.name, pct: 0 });
  try {
    for (let i = 0; i < totalChunks; i++) {
      const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append("chunk", blob, file.name);
      await postWithRetry("/admin/upload/chunk", fd);
      report(Math.round(((i + 1) / totalChunks) * 92));
    }
    const fd2 = new FormData();
    fd2.append("upload_id", uploadId);
    fd2.append("filename", file.name);
    fd2.append("content_type", file.type || "application/octet-stream");
    const { data } = await postWithRetry("/admin/upload/complete", fd2);
    report(100);
    setTimeout(() => emitUpload({ active: false }), 900);
    return data;
  } catch (e) {
    emitUpload({ active: false, error: true });
    throw e;
  }
}
